/**
 * **전투 기록** 화면: 남겨 둔 판(`meta/replays.ts` — 최근 다섯 판과 전과가 가장 높은 한 판)을 골라 다시 본다. 출정 화면의
 * "전투 기록"에서 온다.
 *
 * 한 판이 한 줄이다: 어느 판인가 · 이겼나 · 전과 · 길이 · 언제. 누르면 다시 보기가 열린다(`main.ts`의 `openReplay`).
 */
import { clockText } from '../game/replay-player.ts';
import { bestRecord } from '../meta/replays.ts';
import type { ReplayRecord } from '../meta/replays.ts';
import { button, clear, el } from './dom.ts';

/** "방금", "3분 전", "2시간 전", "어제", "5일 전". 화면만 쓰는 글이다. */
export function agoText(at: number, now: number): string {
  const min = Math.floor(Math.max(0, now - at) / 60_000);
  if (min < 1) return '방금';
  if (min < 60) return `${min}분 전`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '어제' : `${days}일 전`;
}

export function renderRecords(
  root: HTMLElement,
  records: readonly ReplayRecord[],
  actions: { onPlay: (rec: ReplayRecord) => void; onBack: () => void },
): void {
  const best = bestRecord(records);
  const now = Date.now();
  // 새 판이 위. 따로 남긴 최고 전과 판도 제 때의 자리에 선다.
  const list = [...records].sort((a, b) => b.at - a.at);
  clear(root);
  root.append(
    el(
      'div',
      { class: 'result-wrap' },
      el(
        'header',
        { class: 'result-head' },
        el('span', { class: 'eyebrow' }, '전투 기록'),
        el('h1', {}, '지난 판 다시 보기'),
      ),
      list.length === 0
        ? el(
            'section',
            { class: 'panel pick-note' },
            el('p', {}, '아직 남긴 판이 없다.'),
            el('p', { class: 'dim' }, '판을 끝내면 여기에 남는다 — 최근 다섯 판과 전과가 가장 높은 한 판.'),
          )
        : el(
            'div',
            { class: 'record-list', role: 'list' },
            ...list.map((r) => {
              const card = button('', () => actions.onPlay(r), 'record-card');
              card.append(
                el(
                  'span',
                  { class: 'record-top' },
                  el('b', {}, r.title),
                  el('span', { class: `record-won ${r.won ? 'good' : r.fled ? '' : 'bad'}` }, r.won ? '승리' : r.fled ? '물러남' : '패배'),
                  r === best ? el('span', { class: 'record-best' }, '최고 전과') : null,
                ),
                el(
                  'span',
                  { class: 'record-sub' },
                  // 토벌에는 전과가 없다.
                  r.raid ? null : el('span', {}, `전과 ${r.merit.toLocaleString()}`),
                  el('span', {}, `길이 ${clockText(r.replay.ticks)}`),
                  el('span', {}, agoText(r.at, now)),
                ),
                el('span', { class: 'record-go', 'aria-hidden': 'true' }, '▶'),
              );
              // 줄은 목록의 항목이고 그 안의 단추는 단추다 — 단추에 항목 역할을 덮으면 "단추"로 읽히지 않는다(리뷰가 짚었다).
              return el('div', { role: 'listitem' }, card);
            }),
          ),
      list.length > 0
        ? el('p', { class: 'dim record-note' }, '최근 다섯 판과 전과가 가장 높은 한 판을 남긴다. 게임의 규칙이 바뀌는 업데이트 뒤에는 옛 기록을 볼 수 없어 지운다.')
        : null,
      el('div', { class: 'result-actions' }, button('출정 준비로 돌아간다', actions.onBack, 'primary')),
    ),
  );
}
